import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Box, Button, Tooltip } from "@mui/material";
import { debounce, isEqual } from "lodash";
import { CreateExcelTemplate } from "services/models";
import ModalController from "@shared-components/modal/ModalController";
import "./CreateTemplatePreview.css";

interface IProps {
  data: Array<CreateExcelTemplate>;
  tableRowsHeight: Array<string>;
  coordinate: string;
  handleClick: (coordinate: string, value: string) => void;
  draggable?: boolean;
  handleDrag?: (coordinates: Array<string>) => void;
  checkDragText?: boolean;
  selectedCoordinate?: Array<string>;
}

let tableWidth = 0;
let tableHeight = 0;

const CreateTemplatePreview = (
  {
    data,
    tableRowsHeight,
    coordinate,
    handleClick,
    draggable = false,
    handleDrag = () => {},
    checkDragText = true,
    selectedCoordinate = [],
  }: IProps,
  ref: any,
) => {
  const [currentCoordinate, setCurrentCoordinate] = useState<Array<string>>([]);
  const [colIndices, setColIndices] = useState<Array<Array<number>>>([]);
  const [isDrag, setIsDrag] = useState(false);
  const refStartCell = useRef<{
    rowIndex: number;
    cellIndex: number;
    rowspan: number;
    colspan: number;
  }>();
  const refEndCell = useRef<{
    rowIndex: number;
    cellIndex: number;
    rowspan: number;
    colspan: number;
  }>();

  useImperativeHandle(ref, () => ({
    setSelectedCoordinate: (value: string[]) => {
      setCurrentCoordinate([...value]);
    },
  }));

  useEffect(() => {
    if (draggable) {
      setColIndices(scanTable(data));
    }
  }, [data, draggable]);

  useEffect(() => {
    setCurrentCoordinate([...selectedCoordinate]);
  }, []);

  useEffect(() => {
    handleDrag(currentCoordinate);
  }, [currentCoordinate]);

  const unescapeHTML = (html: string) => {
    var el = document.createElement("textarea");
    el.innerHTML = html;
    return el.textContent;
  };

  const handleMouseDown = (e: any) => {
    if (draggable && e.target.tagName == "TD") {
      setIsDrag(true);
      refStartCell.current = {
        rowIndex: e.target.parentNode.rowIndex,
        cellIndex: e.target.cellIndex,
        rowspan: e.target.rowSpan,
        colspan: e.target.colSpan,
      };
    }
  };

  const handleMouseUp = () => {
    setIsDrag(false);
  };

  const handleMouseMove = (e: any) => {
    if (draggable && isDrag && e.target.tagName == "TD") {
      refEndCell.current = {
        rowIndex: e.target.parentNode.rowIndex,
        cellIndex: e.target.cellIndex,
        rowspan: e.target.rowSpan,
        colspan: e.target.colSpan,
      };
      changeSelectCells();
    }
  };

  const scanTable = (rows: Array<CreateExcelTemplate>) => {
    const arr: Array<Array<number>> = [];
    const colIndices: Array<Array<number>> = [];
    for (var y = 0; y < rows.length; y++) {
      var row = rows[y];
      let keys = Object.keys(row);
      let key = keys && keys.length > 0 ? keys[0] : "";
      var table_cells = row[key];
      for (var x = 0; x < table_cells.length; x++) {
        var cell = table_cells[x],
          xx = x,
          tx,
          ty;

        for (; arr[y] && arr[y][xx]; ++xx);

        for (tx = xx; tx < xx + (cell.colSpan || 1); ++tx) {
          for (ty = y; ty < y + (cell.rowSpan || 1); ++ty) {
            if (!arr[ty]) arr[ty] = [];
            arr[ty][tx] = 1;

            if (!colIndices[y]) colIndices[y] = [];
            colIndices[y][x] = xx;
          }
        }
      }
    }
    return colIndices;
  };

  const dragCells = () => {
    if (!refStartCell.current || !refEndCell.current) return;
    const startCell = refStartCell.current;
    const endCell = refEndCell.current;
    const currentCells: Array<string> = [];
    var includesText = false;

    // 基点となるrowとcol
    let startRowIndex = startCell.rowIndex;
    if (startRowIndex > endCell.rowIndex) {
      startRowIndex = endCell.rowIndex;
    }

    let startColIndex = colIndices[startCell.rowIndex][startCell.cellIndex];
    if (startColIndex > colIndices[endCell.rowIndex][endCell.cellIndex]) {
      startColIndex = colIndices[endCell.rowIndex][endCell.cellIndex];
    }

    let endRowIndex = startCell.rowIndex + startCell.rowspan - 1;
    if (endRowIndex < endCell.rowIndex + endCell.rowspan - 1) {
      endRowIndex = endCell.rowIndex + endCell.rowspan - 1;
    }

    let endColIndex =
      colIndices[startCell.rowIndex][startCell.cellIndex] +
      startCell.colspan -
      1;
    if (
      endColIndex <
      colIndices[endCell.rowIndex][endCell.cellIndex] + endCell.colspan - 1
    ) {
      endColIndex =
        colIndices[endCell.rowIndex][endCell.cellIndex] + endCell.colspan - 1;
    }

    //対象セルの抽出
    const dragSelectedCells = () => {
      for (let i = 0; i <= endRowIndex; i++) {
        let keys = Object.keys(data[i]);
        let key = keys && keys.length > 0 ? keys[0] : "";
        var table_cells = data[i][key];
        for (let j = 0; j < table_cells.length; j++) {
          //範囲拡張チェック
          //colIndexの延長処理
          if (
            (i <= startRowIndex &&
              startRowIndex <= i + table_cells[j].rowSpan - 1) ||
            (i <= startRowIndex &&
              startRowIndex <= i + table_cells[j].rowSpan - 1) ||
            (startRowIndex <= i &&
              i + table_cells[j].rowSpan - 1 <= endRowIndex)
          ) {
            if (
              colIndices[i][j] < startColIndex &&
              startColIndex <= colIndices[i][j] + table_cells[j].colSpan - 1
            ) {
              startColIndex = colIndices[i][j];
              dragSelectedCells();
            }

            if (
              colIndices[i][j] <= endColIndex &&
              endColIndex < colIndices[i][j] + table_cells[j].colSpan - 1
            ) {
              endColIndex = colIndices[i][j] + table_cells[j].colSpan - 1;
              dragSelectedCells();
            }
          }

          //rowIndexの延長処理
          if (
            (colIndices[i][j] <= startColIndex &&
              startColIndex <= colIndices[i][j] + table_cells[j].colSpan - 1) ||
            (colIndices[i][j] <= endColIndex &&
              endColIndex <= colIndices[i][j] + table_cells[j].colSpan - 1) ||
            (startColIndex <= colIndices[i][j] &&
              colIndices[i][j] + table_cells[j].colSpan - 1 <= endColIndex)
          ) {
            if (
              i < startRowIndex &&
              startRowIndex <= i + table_cells[j].rowSpan - 1
            ) {
              startRowIndex = i;
              dragSelectedCells();
            }

            if (
              i <= endRowIndex &&
              endRowIndex < i + table_cells[j].rowSpan - 1
            ) {
              endRowIndex = i + table_cells[j].rowSpan - 1;
              dragSelectedCells();
            }
          }

          if (
            startRowIndex <= i &&
            i <= endRowIndex &&
            startColIndex <= colIndices[i][j] &&
            colIndices[i][j] <= endColIndex
          ) {
            currentCells.push(table_cells[j].coordinate);
            if (!includesText) includesText = table_cells[j].value !== "";
          }
        }
      }
    };
    dragSelectedCells();

    if (!isEqual(currentCells, currentCoordinate))
      setCurrentCoordinate(currentCells);
    if (!isDrag) {
      refStartCell.current = undefined;
      refEndCell.current = undefined;
      if (checkDragText && includesText) {
        showErrorIncludesText();
        setCurrentCoordinate(() => []);
      }
    }
  };

  const showErrorIncludesText = () => {
    ModalController.show({
      message: "文字が入力されているカラムは選択できません",
      visibleButton2: true,
    });
  };

  const changeSelectCells = useCallback(debounce(dragCells, 0), [dragCells]);

  useEffect(() => {
    if (!isDrag) dragCells();
  }, [isDrag]);

  return (
    <>
      {draggable && (
        <Box mb={1} sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button onClick={() => setCurrentCoordinate([])}>
            入力範囲クリア
          </Button>
        </Box>
      )}
      <Box
        component={"div"}
        style={{ border: "1px solid", display: "inline-flex" }}
      >
        <table
          id="table-template-create"
          cellPadding={0}
          cellSpacing={0}
          style={{
            borderCollapse: "collapse",
            tableLayout: "fixed",
            border: 0,
            width: tableWidth + "px",
            height: tableHeight + "px",
            display: "block",
          }}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onMouseMove={handleMouseMove}
        >
          <tbody>
            {data &&
              data.map((item, i) => {
                let keys = Object.keys(item);
                let key = keys && keys.length > 0 ? keys[0] : "";
                if (i == 0) {
                  // 初期化
                  tableHeight = 0;
                  tableWidth = 0;
                }
                if (tableWidth == 0) {
                  let w = 0;
                  item[key].forEach((cell) => {
                    w = w + Number(cell.width.replace("px", ""));
                  });
                  tableWidth = w;
                }
                if (item[key] && item[key].length > 0)
                  tableHeight += Number(item[key][0].height.replace("px", ""));

                return (
                  <tr key={i} style={{ height: tableRowsHeight[i] + "px" }}>
                    {item[key].map((cell, n) => (
                      <Tooltip
                        title={cell.coordinate}
                        arrow
                        PopperProps={{
                          modifiers: [
                            {
                              name: "offset",
                              options: {
                                offset: [0, -8],
                              },
                            },
                          ],
                        }}
                        key={n}
                      >
                        <td
                          style={{
                            width: cell.width,
                            height: cell.height,
                            textAlign: cell.textAlign,
                            verticalAlign: cell.verticalAlign,
                            borderTop: cell.border.top
                              ? cell.border.top.style
                              : undefined,
                            borderTopWidth: cell.border.top
                              ? cell.border.top.width
                              : undefined,
                            borderTopColor: cell.border.top
                              ? "#" + cell.border.top.color
                              : undefined,
                            borderLeft: cell.border.left
                              ? cell.border.left.style
                              : undefined,
                            borderLeftWidth: cell.border.left
                              ? cell.border.left.width
                              : undefined,
                            borderLeftColor: cell.border.left
                              ? "#" + cell.border.left.color
                              : undefined,
                            borderRight: cell.border.right
                              ? cell.border.right.style
                              : undefined,
                            borderRightWidth: cell.border.right
                              ? cell.border.right.width
                              : undefined,
                            borderRightColor: cell.border.right
                              ? "#" + cell.border.right.color
                              : undefined,
                            borderBottom: cell.border.bottom
                              ? cell.border.bottom.style
                              : undefined,
                            borderBottomWidth: cell.border.bottom
                              ? cell.border.bottom.width
                              : undefined,
                            borderBottomColor: cell.border.bottom
                              ? "#" + cell.border.bottom.color
                              : undefined,
                            backgroundColor: currentCoordinate.includes(
                              cell.coordinate,
                            )
                              ? "#f5bf7f"
                              : coordinate === cell.coordinate
                              ? "#7fd5d1"
                              : "#" + cell.bg_color,
                          }}
                          colSpan={cell.colSpan}
                          rowSpan={cell.rowSpan}
                          onClick={(e) => {
                            if (
                              (e.ctrlKey && !e.metaKey) ||
                              (!e.ctrlKey && e.metaKey)
                            ) {
                              if (checkDragText && cell.value !== "") {
                                // 文字が入力されている場合は選択不可
                                showErrorIncludesText();
                              } else {
                                setCurrentCoordinate((prev) => {
                                  const tmp = [...prev];
                                  if (!prev.includes(cell.coordinate)) {
                                    tmp.push(cell.coordinate);
                                  }
                                  return tmp;
                                });
                              }
                            } else {
                              handleClick(cell.coordinate, cell.value);
                            }
                          }}
                        >
                          <span
                            style={{
                              color: "#" + cell.font.color,
                              fontSize: cell.font.size,
                              fontFamily: cell.font.fontFamily,
                              fontWeight: cell.font.weight,
                              fontStyle: cell.font.style,
                              textDecoration: cell.font.underline ?? undefined,
                              userSelect: "none",
                            }}
                            className="IgnoreExtractRuleTarget"
                          >
                            {cell.value.split(/<br>|\n/g).map((text, i) => (
                              <div key={i}>{unescapeHTML(text)}</div>
                            ))}
                          </span>
                        </td>
                      </Tooltip>
                    ))}
                  </tr>
                );
              })}
          </tbody>
        </table>
      </Box>
    </>
  );
};
export default forwardRef(CreateTemplatePreview);
